import { CriteriaState } from './types';
import { mergeWithParsedConfig } from './stateSchema';
import { validateCriteria as legacyValidateCriteria } from '../../../utils/CriteriaHelpers';
import { duplicateFieldMessage, findDuplicateDomains } from './domainAffiliateDuplicates';

export type CriteriaValidationErrors = Record<string, unknown>;

export interface CriteriaValidationResult {
  errors: CriteriaValidationErrors;
  hasErrors: boolean;
  summary: string | null;
}

const toOptionalNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const toStringArray = (values: unknown[] | null | undefined): string[] => {
  if (!values) return [];
  return values
    .map((value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'string') return value.trim();
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
        const val = (value as Record<string, unknown>).value;
        return typeof val === 'string' || typeof val === 'number' ? String(val) : null;
      }
      return null;
    })
    .filter((value): value is string => Boolean(value && value.length > 0));
};

const parseDayHourRestrictions = (raw: CriteriaState['dayHourRestrictions']): Array<Record<string, unknown>> => {
  if (!raw) return [];

  const ensureEntry = (day: string, value: unknown) => {
    if (value && typeof value === 'object') {
      const restriction = value as Record<string, unknown>;
      return {
        restrictionDay: day,
        restriction: restriction.restriction ?? '',
        restrictionStart: restriction.restrictionStart ?? '',
        restrictionEnd: restriction.restrictionEnd ?? '',
      };
    }

    if (typeof value === 'string') {
      return {
        restrictionDay: day,
        restriction: value,
        restrictionStart: '',
        restrictionEnd: '',
      };
    }

    return {
      restrictionDay: day,
      restriction: '',
      restrictionStart: '',
      restrictionEnd: '',
    };
  };

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((value, index) => ensureEntry(String(index), value));
      }
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed).map(([day, value]) => ensureEntry(day, value));
      }
    } catch (error) {
      return [];
    }
  }

  if (Array.isArray(raw)) {
    return raw.map((value, index) => ensureEntry(String(index), value));
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>).map(([day, value]) => ensureEntry(day, value));
  }

  return [];
};

const parseRandomFailureProbabilities = (
  values: CriteriaState['randomFailureProbabilities']
): Array<Record<string, unknown>> => {
  if (!Array.isArray(values)) return [];

  return values.map((entry) => ({
    probability: entry?.probability ?? '',
    condition: entry?.condition ?? '',
  }));
};

const normalisePreviousRegistrations = (configuration: CriteriaState['previousRegistrations']) => {
  if (!configuration) return null;

  const entries = Array.isArray(configuration.previousRegistrations)
    ? configuration.previousRegistrations.map((entry) => ({
        includeExcludeAction: entry?.includeExcludeAction ?? '',
        domains: toStringArray(entry?.domains ?? []),
        previousRegistrationsMin: entry?.previousRegistrationsMin ?? null,
        previousRegistrationsMax: entry?.previousRegistrationsMax ?? null,
      }))
    : [];

  return {
    andOrLogic: configuration.andOrLogic ?? null,
    previousRegistrations: entries,
  };
};

const normaliseDomainAffiliates = (configuration: CriteriaState['domainAffiliates']) => {
  if (!configuration) return null;

  const entries = Array.isArray(configuration.domainAffiliateEntries)
    ? configuration.domainAffiliateEntries.map((entry) => ({
        includeExcludeAction: entry?.includeExcludeAction ?? '',
        domains: toStringArray(entry?.domains ?? []),
        affiliates: toStringArray(entry?.affiliates ?? []),
      }))
    : [];

  return {
    domainAffiliateEntries: entries,
  };
};

const normaliseUserAttributes = (attributes: CriteriaState['userAttributes']) => {
  if (!attributes) return null;
  return attributes;
};

const normaliseAffSubIds = (configuration: CriteriaState['affSubIDConfiguration']) => {
  if (!configuration) return null;

  // Use new structure with andOrLogic and subIdList
  return {
    andOrLogic: configuration.andOrLogic ?? '',
    subIdList: Array.isArray(configuration.subIdList)
      ? configuration.subIdList.map((entry) => ({
          includeExclude: entry.includeExclude ?? null,
          affiliate: entry.affiliate ?? null,
          subIds: Array.isArray(entry.subIds)
            ? entry.subIds
            : typeof entry.subIds === 'string'
              ? entry.subIds
                  .split('\n')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0)
              : [],
        }))
      : [],
    // Keep legacy fields for backward compatibility with validation
    includeExclude: configuration.includeExclude ?? 'None',
    affiliate: configuration.affiliate ?? '',
    subIds: typeof configuration.subIds === 'string' ? configuration.subIds : '',
  };
};

const normaliseIpqsConfiguration = (configuration: CriteriaState['ipqsConfiguration']) => {
  if (!configuration) {
    return {
      shouldProcess: 'Disabled',
      email: null,
      ip: null,
      phone: null,
      fingerprint: null,
    };
  }

  const { shouldProcess, email, ip, phone, fingerprint } = configuration;

  return {
    shouldProcess: shouldProcess ? 'Enabled' : 'Disabled',
    email: toOptionalNumber(email),
    ip: toOptionalNumber(ip),
    phone: toOptionalNumber(phone),
    fingerprint: toOptionalNumber(fingerprint),
  };
};

const normaliseInternalPrePingConfiguration = (configuration: CriteriaState['internalPrePings']) => {
  if (!configuration) return null;

  const internalPrePings = Array.isArray(configuration.internalPrePings)
    ? configuration.internalPrePings.map((entry) => ({
        prePingSuppressionType: entry?.prePingSuppressionType ?? '',
        prePingSuppressionAmount: entry?.prePingSuppressionAmount ?? null,
        prePingSuppressionLevel: entry?.prePingSuppressionLevel ?? '',
        prePingSuppressionAnswer: entry?.prePingSuppressionAnswer ?? '',
        prePingSuppressionMaxLimit: entry?.prePingSuppressionMaxLimit ?? null,
        additionalOffers: Array.isArray(entry?.additionalOffers) ? entry.additionalOffers : [],
        additionalOfferActions: Array.isArray(entry?.additionalOfferActions) ? entry.additionalOfferActions : [],
      }))
    : [];

  return {
    andOrLogic: configuration.andOrLogic ?? 'AND',
    internalPrePings,
  };
};

const buildLegacyCriteriaValues = (state: CriteriaState): Record<string, unknown> => {
  const minAge = toOptionalNumber(state.minAge);
  const maxAge = toOptionalNumber(state.maxAge);
  const dailyLimitNumber = toOptionalNumber(state.dailyLimit);

  const legacy: Record<string, unknown> = {
    minAge,
    maxAge,
    criteriaMinAge: minAge,
    criteriaMaxAge: maxAge,
    userIdLastDigitIncludeExclude: state.userIdLastDigitIncludeExclude ?? 'None',
    userIdLastDigitStringValues: toStringArray(state.userIdLastDigitValues as unknown[]),
    criteriaIncludeOrExcludeUserIdLastDigit: state.userIdLastDigitIncludeExclude ?? 'None',
    criteriaUserIdLastDigitString: toStringArray(state.userIdLastDigitValues as unknown[]),
    previousRegistrationConfiguration: normalisePreviousRegistrations(state.previousRegistrations),
    ipqsConfiguration: normaliseIpqsConfiguration(state.ipqsConfiguration),
    criteriaIncludeOrExcludeDevices: state.devicesIncludeExclude ?? 'None',
    criteriaDevices: state.devices ?? [],
    criteriaAibListIncludeExclude: state.aibListIncludeExclude ?? 'None',
    criteriaAibList: state.aibList ?? [],
    criteriaIncludeOrExcludeBrowsers: state.browsersIncludeExclude ?? 'None',
    criteriaBrowsers: state.browsers ?? [],
    criteriaIncludeOrExcludeDomains: state.domainsIncludeExclude ?? 'None',
    criteriaDomains: state.domains ?? [],
    domainAffiliateConfiguration: normaliseDomainAffiliates(state.domainAffiliates),
    criteriaIncludeOrExcludeOperatingSystems: state.operatingSystemIncludeExclude ?? 'None',
    criteriaOperatingSystems: state.operatingSystems ?? [],
    criteriaIncludeOrExcludeStates: state.statesIncludeExclude ?? 'None',
    criteriaStates: state.statesValues ?? [],
    criteriaIncludeOrExcludeZips: state.zipsIncludeExclude ?? 'None',
    criteriaZips: state.zipsValues ?? '',
    emailDomainsIncludeExclude: state.emailDomainsIncludeExclude ?? 'None',
    emailDomains: state.emailDomains ?? [],
    criteriaIncludeOrExcludeEmailDomains: state.emailDomainsIncludeExclude ?? 'None',
    criteriaEmailDomains: state.emailDomains ?? [],
    criteriaIncludeOrExcludeUserAttributes: state.userAttributesIncludeExclude ?? 'None',
    userAttributeRules: normaliseUserAttributes(state.userAttributes),
    criteriaIncludeOrExcludeUrlParameters: state.urlParametersIncludeExclude ?? 'None',
    urlParameterRules: state.urlParameterRules ?? null,
    criteriaIncludeOrExcludeUserAgent: state.userAgentIncludeExclude ?? 'None',
    userAgentRules: state.userAgentRules ?? null,
    criteriaIncludeOrExcludeAffNames: state.affiliateNamesIncludeExclude ?? 'None',
    criteriaAffNames: state.affiliateNames ?? [],
    qualityScoreCriteria: state.qualityScore ?? { minScore: '', maxScore: '' },
    criteriaQuestion: state.question ?? '',
    criteriaQuestionAnswers: state.questionAnswers ?? [],
    criteriaLandingPageGroupIncludeExclude: state.landingPageGroupIncludeExclude ?? 'None',
    criteriaLandingPageGroupIds: state.landingPageGroupIds ?? [],
    criteriaLandingPageIncludeExclude: state.landingPageIncludeExclude ?? 'None',
    criteriaLandingPageIds: state.landingPageIds ?? [],
    criteriaDailyLimit: dailyLimitNumber,
    criteriaDailyLimitAnswers:
      Array.isArray(state.dailyLimitAnswers) && state.dailyLimitAnswers.length > 0 ? state.dailyLimitAnswers : null,
    dailyLimit: dailyLimitNumber,
    dailyLimitAnswers: state.dailyLimitAnswers ?? [],
    capInterval: state.capInterval ?? '',
    capAmount: state.capAmount ?? null,
    criteriaDayHourRestrictions: parseDayHourRestrictions(state.dayHourRestrictions),
    randomFailureProbabilities: parseRandomFailureProbabilities(state.randomFailureProbabilities),
    affSubIDConfiguration: normaliseAffSubIds(state.affSubIDConfiguration),
    criteriaNullSourceNewUserIncludeExclude: state.nullSourceNewUserIncludeExclude ?? 'None',
    criteriaUserDataIncludeExclude: state.requiredUserDataIncludeExclude ?? 'None',
    criteriaRequiredUserData: state.requiredUserDataValues ?? [],
    internalPrePingConfiguration: normaliseInternalPrePingConfiguration(state.internalPrePings),
    prePingMethod: state.prePingMethod ?? null,
    prePingHandlerId: state.prePingHandlerId ?? null,
    prePing: state.prePingUrl ?? '',
    prePingUrl: state.prePingUrl ?? '',
    prePingBody: state.prePingBody ?? '',
    prePingCustomHttpStatus: state.prePingCustomHttpStatus ?? '',
    prePingHeader1: state.prePingHeader1 ?? '',
    prePingHeader2: state.prePingHeader2 ?? '',
    prePingHeader3: state.prePingHeader3 ?? '',
    prePingTimeout: state.prePingTimeout ?? '',
    prePingResultSessionCapture: state.prePingResultSessionCapture ?? false,
  };

  return legacy;
};

const hasValidationErrors = (errors: unknown): boolean => {
  if (!errors) return false;
  if (typeof errors === 'string') return errors.trim().length > 0;
  if (typeof errors === 'number' && !Number.isNaN(errors)) return true;
  if (Array.isArray(errors)) {
    return errors.some((entry) => hasValidationErrors(entry));
  }
  if (typeof errors === 'object') {
    return Object.values(errors as Record<string, unknown>).some((value) => hasValidationErrors(value));
  }
  return false;
};

const extractFirstError = (errors: unknown): string | null => {
  if (!errors) return null;
  if (typeof errors === 'string' && errors.trim().length > 0) return errors;
  if (Array.isArray(errors)) {
    for (const entry of errors) {
      const result = extractFirstError(entry);
      if (result) return result;
    }
    return null;
  }
  if (typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      const result = extractFirstError(value);
      if (result) return result;
    }
    return null;
  }
  return null;
};

export const validateCriteriaState = (state: CriteriaState): CriteriaValidationResult => {
  const legacyValues = buildLegacyCriteriaValues(state);
  const errors: CriteriaValidationErrors = (legacyValidateCriteria(legacyValues) || {}) as CriteriaValidationErrors;

  // Validate capConfiguration (new field - not part of legacy validation)
  if (state.capConfiguration?.caps && Array.isArray(state.capConfiguration.caps)) {
    const capErrors: Array<Record<string, string>> = [];
    let hasCapErrors = false;
    state.capConfiguration.caps.forEach((cap, index) => {
      capErrors[index] = {};
      if (cap.amount == null || cap.amount < 1) {
        capErrors[index].amount = 'Cap amount must be greater than 0.';
        hasCapErrors = true;
      }
    });
    if (hasCapErrors) {
      errors.capConfiguration = { caps: capErrors };
    }
  }

  // ZET-4737: a domain may appear in only one Domain-specific Affiliates entry.
  const duplicateDomains = findDuplicateDomains(state.domainAffiliates?.domainAffiliateEntries);
  if (duplicateDomains.length > 0) {
    const container = (errors.domainAffiliateConfiguration ?? {}) as Record<string, unknown>;
    const entryErrors = (
      Array.isArray(container.domainAffiliateEntries) ? [...(container.domainAffiliateEntries as unknown[])] : []
    ) as Array<Record<string, unknown>>;
    (state.domainAffiliates?.domainAffiliateEntries ?? []).forEach((_entry, index) => {
      const message = duplicateFieldMessage(duplicateDomains, index);
      if (!message) return;
      entryErrors[index] = { ...(entryErrors[index] ?? {}), domains: message };
    });
    errors.domainAffiliateConfiguration = { ...container, domainAffiliateEntries: entryErrors };
  }

  const hasErrors = hasValidationErrors(errors);
  const summary = hasErrors
    ? duplicateDomains.length > 0
      ? 'Resolve the duplicate domains in Domain-specific Affiliates to save.'
      : (extractFirstError(errors) ?? 'Criteria contains invalid settings.')
    : null;

  return {
    errors,
    hasErrors,
    summary,
  };
};

export const validateCriteriaConfiguration = (configuration?: string | null): CriteriaValidationResult => {
  if (!configuration) {
    return {
      errors: {},
      hasErrors: false,
      summary: null,
    };
  }

  try {
    const parsed = JSON.parse(configuration);
    const state = mergeWithParsedConfig(parsed ?? {});
    return validateCriteriaState(state);
  } catch (error) {
    const summary = 'Unable to parse criteria configuration.';
    return {
      errors: { _error: summary },
      hasErrors: true,
      summary,
    };
  }
};

export const criteriaValidationHasErrors = hasValidationErrors;
export const criteriaValidationFirstMessage = extractFirstError;
export const normaliseCriteriaStateForLegacyValidation = buildLegacyCriteriaValues;
